import { AlertController, ModalController } from "@ionic/angular";
import { DateTime, Interval } from "luxon";
import { AdjustOdometerPage } from "src/app/pages/adjust-odometer/adjust-odometer.page";
import { Job, BaseJob, JobType, Location, NoShowOptions, NoShowReasons, StopTypeEnum } from "../types";
import { SignaturePage } from "src/app/pages/signature/signature.page";
import { LocationUtilityService, RouteProviderService, TranslateService } from "src/app/services";
import { environment } from "src/environments/environment";

export function getTimeForCardHeader({ start, end, translateSvc }: { start: DateTime, end?: DateTime, translateSvc: TranslateService }): string {
  let time = `${ start?.toFormat('HH:mm') || '' }`;
  if (end) {
    time += ` ${ translateSvc.translate('LABEL.to') } ${ end.toFormat('HH:mm') }`;
  }
  return time;
}

/**
 * Diffs provided DateTime with current time,
 * return true if the provided DateTime is in the past
 */
export function isInThePast(dateTime: DateTime): boolean {
  if (!dateTime) { return false; }
  const now = DateTime.now();
  return now.valueOf() > dateTime.valueOf();
}

/** Prompts user for odometer update and return whether odometer was updated or not */
export async function presentOdometer(modalController: ModalController): Promise<boolean> {
  console.debug('present odo');
  const odometer = await modalController.create({
    cssClass: 'parascope-odometer-modal',
    component: AdjustOdometerPage,
    backdropDismiss: false,
  });
  await odometer.present();
  const { data, role } = await odometer.onDidDismiss();
  return role === 'submit';
}

/** get signature */
export type SignatureResponse = { role: 'submit' | 'cancel', data: { signature: string, signatureReason: string } | null }
export async function getSignature(
  { job, modalController }
  : { job: BaseJob, modalController: ModalController },
): Promise<SignatureResponse> {
  const modal = await modalController.create({
    component: SignaturePage,
    componentProps: { job },
    cssClass: 'parascope-full-modal',
    backdropDismiss: false,
  });
  await modal.present();
  const dialog = modal.shadowRoot.querySelector('[role="dialog"]');
  dialog.setAttribute('aria-label', 'signature');
  const result = await modal.onDidDismiss();
  return {
    role: result.role === 'cancel' ? 'cancel': 'submit',
    data: {
      signature: result?.data?.signature || null,
      signatureReason: result?.data?.signatureReason || null,
    },
  };
}


/** Trims to just the first letter of the firstName when trimName is enabled  */
export function handleName(first: string, last: string): string {
  if (!first && !last) { return ''; }
  if (environment.features.trimName) return `${last}, ${first?.[0]}.`;
  return `${last}, ${first}`;
}

/** Checks that location is geocoded and does not go to null island */
export function hasValidGeo(location: Location) {
  const { latitude, longitude } = location?.coordinate || {};
  return !!latitude && !!longitude;
}

//  #region States

export function canOnTheWayJob({ jobType, arriveTime, onTheWayTime, jobEnabled }: BaseJob): boolean {
  if (!environment.features.hasOnTheWay) { return false; }
  if (jobType === JobType.Pickup && !arriveTime && !onTheWayTime) return true;
  return false;
}

export function canArrive(
  { arriveTime, flagStopId, onTheWayTime, jobType, jobEnabled }:
  { arriveTime: string, flagStopId?: string | number, onTheWayTime: string, jobType?: JobType, jobEnabled?: boolean },
): boolean {
  const onTheWayCompleted = (jobType === JobType.DropOff && jobEnabled) || !environment.features.hasOnTheWay || !!onTheWayTime;
  return !arriveTime && !flagStopId && onTheWayCompleted && (jobType !== JobType.DropOff || jobEnabled);
}

export function canOnBoardJob({ jobType, arriveTime, performTime, stopType }: BaseJob): boolean {
  return (jobType === JobType.Pickup || stopType === StopTypeEnum.MONITOR_PICKUP) && !!arriveTime && (environment.features.hasDepart ? !performTime : true);
}

export function canNoShowJob({ jobType, arriveTime, performTime }: BaseJob): boolean {
  return jobType === JobType.Pickup && !!arriveTime && (environment.features.hasDepart ? !performTime : true);
}

// #endregion

/** Get similar job list */
export function getSimilarJobs(
  { can, anchor, routeEvents }
  : { can: (job: BaseJob) => boolean, anchor: Job, routeEvents: any[] },
): Job[] {
  //  no need to calculate anything if anchor has no valid coordinates
  //  because we can't vet it for distance against other jobs
  if (!hasValidGeo(anchor.location)) { return []; }

  const { timeTolerance, distanceTolerance } = environment.features;

  //  we only care about Jobs in this context
  const jobs: Job[] = routeEvents.filter(routeEvent => routeEvent instanceof Job);

  return jobs.filter(_routeEvent => {
    //  ignore self
    if (anchor.value.rideId === _routeEvent.value.rideId) { return false; }
    //  don't include will calls
    if (_routeEvent.value.willCall) { return false; }
    //  don't include anything that doesn't have scheduledTime
    if (!_routeEvent.scheduledStart) { return false; }
    //  don't include if not of the same type (pickup vs dropoff)
    if (_routeEvent.value.jobType !== anchor.value.jobType) { return false; }
    //  don't include if action cannot be performed
    if (!can(_routeEvent.value)) { return false; }

    //  calculate time diff and exclude if jobs are too far apart in time
    const interval = Interval.fromDateTimes(_routeEvent.scheduledStart, anchor.scheduledStart);
    const timeDiff = interval?.isValid ? interval.count('minutes') : Infinity;
    if (timeDiff > timeTolerance) { return false; }

    //  don't include if job does not have valid coordinates
    if (!hasValidGeo(_routeEvent.value.location)) { return false; }

    //  calculate distance between and exclude if jobs are too far apart
    const distanceBetween = LocationUtilityService.euclideanDistanceInMiles(
      _routeEvent.location.coordinate.latitude,
      _routeEvent.location.coordinate.longitude,
      anchor.location.coordinate.latitude,
      anchor.location.coordinate.longitude,
    );
    //  don't include we we are too far apart in distance
    if (distanceBetween > distanceTolerance) { return false; }

    //  we have exhausted reasons not to include it so must be a mach
    return true;
  });
}

/** Check to see and notify if necessary about including similar jobs */
export async function includeSimilarJobs(
  { additionalJobCount, dialog: { header, message }, alertController }: { additionalJobCount: number, dialog: { header: string, message: string }, alertController: AlertController },
): Promise<boolean> {

  if (!additionalJobCount) { return false; }

  const { notifyAdditionalJobs } = environment.features;
  if (!notifyAdditionalJobs) { return true; }

  let response = false;
  const alert = await alertController.create({
    cssClass: 'alert-wrapperz',
    header,
    message,
    buttons: [
      {
        text: this.translateSvc.translate('LABEL.yes'),
        cssClass: 'primary',
        handler: () => response = true,
      }, {
        text: this.translateSvc.translate('LABEL.no'),
        cssClass: 'danger',
        handler: () => response = false,
      },
    ],
  });
  await alert.present();
  await alert.onDidDismiss();
  return response;
}

export function calculateJobFare( job: BaseJob): number {
  if (!job) { return 0; }

  if (environment.features.fareCalculateJob) {
    return calculateRiderFare(job)
      + calculateAdditionalFare(job);
  }
  return job.rideFare;
}

export function calculateRiderFare(job: BaseJob): number {
  if (!job) { return 0; }

  const { rideFare = 0, riderBalanceDebit = 0, riderExternalAccountDebit = 0 } = job;
  return rideFare + riderBalanceDebit + riderExternalAccountDebit;
}

export function calculateAdditionalFare(job: BaseJob): number {
  if (!job) { return 0; }

  const { additionalRideFare = 0, additionalRideBalanceDebit = 0, additionalRideExternalAccountDebit = 0 } = job;
  return additionalRideFare + additionalRideBalanceDebit + additionalRideExternalAccountDebit;
}

/** Format fare to currency in USD */
export function formatCurrency(value: number): string {
  const _value = value || 0;
  return Intl.NumberFormat("en-US", { style: 'currency', currency: 'USD' }).format(_value);
}

//  #region Icons

export type TypeIcon = 'ambulatory' | 'wheelchair' | 'stretcher' | 'lift' | 'flag_stop' | 'time_point' | 'waypoint' | 'break'
  | 'outOfServicePeriod' | 'groupTripMonitor'
export type Icon = { type: string, name: string, svg: boolean, label: string, sortOrder: number }
export const TypeIcons: Record<TypeIcon,Icon> = {
  ambulatory: { type: 'mobility ambulatory', name: 'ambulatory', svg: true,  sortOrder: 1, label: 'MOBILITY.ambulatory' },
  wheelchair: { type: 'mobility wheelchair', name: 'wheelchair', svg: true,  sortOrder: 2, label: 'MOBILITY.wheelchairSeats' },
  stretcher:  { type: 'mobility stretcher',  name: 'stretcher',  svg: true,  sortOrder: 3, label: 'MOBILITY.stretcher' },
  lift:       { type: 'mobility lift',       name: 'lift',       svg: true,  sortOrder: 4, label: 'MOBILITY.lift' },
  time_point: { type: 'flag-stop',           name: 'time',       svg: false, sortOrder: 5, label: 'LABEL.timePoint' },
  flag_stop:  { type: 'flag-stop',           name: 'flagstop',   svg: true,  sortOrder: 6, label: 'LABEL.stop' },
  waypoint:   { type: 'waypoint',            name: 'waypoint',   svg: true,  sortOrder: 7, label: 'LABEL.waypoint' },
  break:      { type: 'break',               name: 'pause',      svg: false, sortOrder: 8, label: 'LABEL.break' },
  outOfServicePeriod: { type: 'outOfServicePeriod', name: 'out-of-service-period', svg: true, sortOrder: 9, label: 'LABEL.outOfServicePeriod' },
  groupTripMonitor:   { type: 'groupTripMonitor', name: 'group-trip-monitor', svg: true, sortOrder: 10, label: 'LABEL.groupTripMonitor' },
} as const;
export type TypeIcons = typeof TypeIcons;

/** get array of mobility Icons for pickup and dropoff card header */
export function getMobilityIcons(job: BaseJob): Icon[] {
  const icons = [];
  if (!job.wheelchairSeats && !job.liftRequired && !job.liftRequired && !job.stretcherCount) { icons.push(TypeIcons.ambulatory); }
  if (job.wheelchairSeats) { icons.push(TypeIcons.wheelchair); }
  if (job.stretcherCount) { icons.push(TypeIcons.stretcher); }
  if (job.liftRequired) { icons.push(TypeIcons.lift );}
  return icons;
}

/** get array of smaller icons for bottom of pickup and dropoff card header */
export function getDetailsIcons(job: BaseJob): Icon[] {
  const icons: Icon[] = [];
  if (job.riderImageId) {
    icons.push({ type: 'details image', name: 'person-circle-outline', svg: false, label: 'ALT_TEXT.hasRiderImage', sortOrder: 1  });
  }
  if (job.rideComments || job.riderComments) {
    icons.push({ type: 'details comments', name: 'chatbubbles', svg: false, label: 'ALT_TEXT.hasComments', sortOrder: 2  });
  }
  return icons;
}

// #endregion

export type Badge = { color: string, class: string, label: string }
/** get array of badges for bottom of the pickup and dropoff card header */
export function getDetailBadges({ jobType, willCall, willCallReady, specialAssistances = [] }: BaseJob, translateSvc: TranslateService): Badge[] {
  const badges: Badge[] = [];
  if (willCall && jobType === JobType.Pickup) {
    const label = willCallReady
      ? `${ translateSvc.translate('LABEL.willCall') } ${ translateSvc.translate('LABEL.ready') }`
      : `${ translateSvc.translate('LABEL.notReady') } ${ translateSvc.translate('LABEL.willCall') }`;
    badges.push({ class: '', color: willCallReady ? 'success' : 'warning', label });
  }
  (specialAssistances || []).sort().forEach(specialInstruction => {
    badges.push({ class: 'special-assistance', color: 'medium', label: specialInstruction });
  });
  return badges;
}

export function getTranslatedNoShowOptionList(job: BaseJob, routeProvider: RouteProviderService, translateService: TranslateService): NoShowOptions[] {
  return routeProvider.getNoShowOptionList(job)
    .map(option => ({
      ...option,
      displayValue: environment.features.translateNoShowOptions
        ? translateService.translate(NoShowReasons[option.id])
        : option.displayValue,
    }));
}

export function getStopTypeLabel(stopType: StopTypeEnum, translateSvc: TranslateService): string {
  switch (stopType) {
    case StopTypeEnum.SCHEDULED_FLAG_STOP     : return translateSvc.translate('LABEL.scheduledStop');
    case StopTypeEnum.PULL_IN                 : return translateSvc.translate('LABEL.pullIn');
    case StopTypeEnum.PULL_OUT                : return translateSvc.translate('LABEL.pullOut');
    case StopTypeEnum.OUT_OF_SERVICE_START    : return `${translateSvc.translate('LABEL.outOfServicePeriod')} ${ translateSvc.translate('ACTION.start')}`;
    case StopTypeEnum.OUT_OF_SERVICE_END      : return `${translateSvc.translate('LABEL.outOfServicePeriod')} ${ translateSvc.translate('ACTION.end')}`;
    case StopTypeEnum.WAYPOINT                : return translateSvc.translate('LABEL.waypoint');
    case StopTypeEnum.MONITOR_PICKUP          : return `${translateSvc.translate('LABEL.groupTripMonitor')} ${ translateSvc.translate('LABEL.pickup') }`;
    case StopTypeEnum.MONITOR_DROPOFF         : return `${translateSvc.translate('LABEL.groupTripMonitor')} ${ translateSvc.translate('ACTION.dropOff') }`;
    case StopTypeEnum.BREAK_WITH_LOCATION     :
    case StopTypeEnum.BREAK_WITHOUT_LOCATION  : return translateSvc.translate('LABEL.break');
    default: return stopType;
  }
}

export function numberIsValidNumberFormat(value: string | undefined | null): boolean {
  if (value === '' || value === undefined || value === null) { return true; }
  /**
   * Validates numeric input including:
   * - Optional negative sign
   * - Optional thousands separators (commas)
   * - Leading/trailing decimals (e.g., .1, 3.)
   * * Logic Breakdown:
   * (?!^-?$)             - Not just a "-"
   * (?!^\.?$)            - Not just a "."
   * -?                   - Optional negative sign
   * (                    - Start of Whole Number group:
   * \d{1,3}(?:,\d{3})*   - EITHER: 1-3 digits followed by comma groups (1,000)
   * |                    - OR:
   * \d*                  - Simple digits (including empty for cases like .1)
   * )                    - End of Whole Number group
   * (\.\d*)?             - Optional decimal point followed by zero or more digits
   * $                    - End of string
   */
  const regex = /^(?!^-?$)(?!^\.?$)-?(?:\d{1,3}(?:,\d{3})*|\d*)(\.\d*)?$/g;
  return regex.test(value);
}
